/**
 * Le fil d'un élève, ligne par ligne : chaque entrée de son historique devient UNE ligne au même squelette — son
 * point, le fait en une ligne, au plus une ligne grise —, rangée à sa minute de la séance. Pur, testé.
 *  - `threadLines` : les lignes, leur point (celui que la tuile montre pour leur dernier moment, sinon celui de leur
 *    sorte), leur minute, la minute de la ligne suivante (jusqu'où va leur part de piste), les points de la tuile
 *    qu'elles portent, et une ligne par sortie de l'appli assez longue pour qu'on la dise ; une sortie plus courte
 *    reste une hachure sur la piste ; et une ligne par entrée dans la salle (le point « Connexion » de la tuile) ;
 *  - `lineContaining` : la ligne d'un point de la tuile, pour y défiler ;
 *  - `railPieces` : ce que la piste montre entre une ligne et la suivante (le mode, une sortie) ;
 *  - `lineCopy` : ce que dit une ligne.
 */
import { tutorThreadPoint } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { type UITutorSessionEntry, type UITutorSessionRibbon } from "../../../ports/primary/ui/types/tutorLive"
import { type ThreadDotKind } from "../../molecules/ThreadDot/ThreadDot"

type Entry = Exclude<UITutorSessionEntry, { kind: "since" }>

/** `next` : la minute de la ligne suivante, ou de maintenant pour la dernière ; absente : la ligne n'a pas d'heure. */
type LineBase = { id: string; dot: ThreadDotKind; eventIds: string[]; next?: number }

export type ThreadLine =
    | (LineBase & { kind: "entry"; entry: Entry; minute: number | null; at: string })
    /** Une sortie de l'appli d'au moins `AWAY_LINE_MIN` minute(s) : `to`, son retour (la minute de maintenant si elle dure). */
    | (LineBase & { kind: "away"; dot: "away"; minute: number; to: number; at: string })
    /** Une entrée dans la salle : le point « Connexion » de la tuile. */
    | (LineBase & { kind: "join"; dot: "join"; minute: number; at: string })

export type ThreadItem = ThreadLine | Extract<UITutorSessionEntry, { kind: "since" }>

/** Une sortie de l'appli plus courte reste une hachure sur la piste, sans ligne (le bruit du fil). */
export const AWAY_LINE_MIN = 1
const isLongAway = (range: { from: number; to: number }) => range.to - range.from >= AWAY_LINE_MIN

/** Les lignes du fil, à l'heure ; le repère « Depuis ton dernier passage » garde sa place. */
export function threadLines(history: UITutorSessionEntry[], ribbon?: UITutorSessionRibbon | null): ThreadItem[] {
    const start = ribbon != null ? Date.parse(ribbon.startedAt) : NaN
    const minuteOf = (iso: string) => {
        const at = Date.parse(iso)
        return Number.isNaN(start) || Number.isNaN(at) ? null : (at - start) / 60_000
    }
    const events = new Map((ribbon?.events ?? []).map((event) => [event.id, event]))
    const items: ThreadItem[] = history.map((entry) => {
        if (entry.kind === "since") return entry
        const eventIds = carriedIds(entry).filter((id) => events.has(id))
        // Le point de son dernier fait : une activité dit où elle en est (la main levée puis le rendu juste : vert).
        const last = eventIds.length > 0 ? events.get(eventIds[eventIds.length - 1]) : undefined
        const dot = last?.kind ?? ownDot(entry)
        return { kind: "entry", id: entry.id, entry, dot, minute: minuteOf(entry.at), at: entry.at, eventIds }
    })
    // Sans début lisible, une sortie ou une entrée n'a pas d'heure : la piste seule la dit.
    const isoAt = (minute: number) => new Date(start + minute * 60_000).toISOString()
    const timed: ThreadLine[] = Number.isNaN(start)
        ? []
        : [
              ...(ribbon?.away ?? []).filter(isLongAway).map((range): ThreadLine => ({
                  kind: "away",
                  id: `away-${range.from}`,
                  dot: "away",
                  minute: range.from,
                  to: range.to,
                  at: isoAt(range.from),
                  eventIds: [],
              })),
              ...(ribbon?.events ?? [])
                  .filter((event) => event.kind === "join")
                  .map((event): ThreadLine => ({
                      kind: "join",
                      id: event.id,
                      dot: "join",
                      minute: event.at,
                      at: isoAt(event.at),
                      eventIds: [event.id],
                  })),
          ]
    for (const line of timed) {
        // Après la dernière ligne qui la précède : l'historique est déjà à l'heure, la ligne s'y glisse.
        const after = items.findIndex(
            (item) => item.kind !== "since" && item.minute != null && item.minute > line.minute
        )
        if (after === -1) items.push(line)
        else items.splice(after, 0, line)
    }
    // La part de piste de chaque ligne va jusqu'à la suivante qui a une heure : un seul passage, du bas vers le haut.
    let next = ribbon?.now
    for (let index = items.length - 1; index >= 0; index -= 1) {
        const item = items[index]
        if (item.kind === "since") continue
        item.next = next
        if (item.minute != null) next = item.minute
    }
    return items
}

/** Les points de la tuile qu'une entrée peut porter (une ligne et son point ont le même id) : le sien, ou ses traces. */
function carriedIds(entry: Entry): string[] {
    return entry.kind === "activity" ? entry.traces.map((trace) => trace.id) : [entry.id]
}

/**
 * Le point d'une entrée que la tuile ne montre pas : celui que la tuile lui donnerait (`tutorThreadPoint`), sinon
 * celui de sa sorte — ce que le masteur a donné, ce qui attend encore, un fait sans signature.
 */
function ownDot(entry: Entry): ThreadDotKind {
    const point = tutorThreadPoint(entry)
    if (point != null) return point.kind
    switch (entry.kind) {
        case "tutor":
            return "sent"
        case "activity":
            return entry.status === "done" ? "ok" : "trace"
        case "trace":
            return entry.quiet === true ? "stale" : "trace"
        default:
            return "waiting"
    }
}

/** La ligne qui porte un point de la tuile. */
export function lineContaining(items: ThreadItem[], eventId: string): ThreadLine | undefined {
    return items.find((item): item is ThreadLine => item.kind !== "since" && item.eventIds.includes(eventId))
}

/**
 * Ce que la piste montre entre une ligne (`from`, sa minute) et la suivante (`to`), en fractions du segment : les plages
 * de mode, les sorties de l'appli qui ont leur ligne (`out`, hachurées jusqu'au retour, pas jusqu'à la ligne suivante),
 * et le centre de celles trop brèves pour la leur (`away`, un trait). Un segment sans durée prend le mode de son
 * instant, en entier, hachuré si l'élève était dehors.
 */
export function railPieces(
    ribbon: Pick<UITutorSessionRibbon, "modes" | "away">,
    from: number,
    to: number
): { modes: RailPiece<"dialogue" | "focused">[]; out: RailPiece[]; away: number[] } {
    const long = (ribbon.away ?? []).filter(isLongAway)
    if (to <= from) {
        const mode = ribbon.modes.find((range) => range.from <= from && from <= range.to)?.mode
        const out = long.some((range) => range.from <= from && from < range.to)
        return {
            modes: mode != null ? [{ top: 0, height: 1, mode }] : [],
            out: out ? [{ top: 0, height: 1 }] : [],
            away: [],
        }
    }
    const span = to - from
    const piece = (range: { from: number; to: number }) => {
        const top = (Math.max(range.from, from) - from) / span
        return { top, height: (Math.min(range.to, to) - from) / span - top }
    }
    const within = (range: { from: number; to: number }) => range.to > from && range.from < to
    const modes = ribbon.modes.filter(within).map((range) => ({ ...piece(range), mode: range.mode }))
    const out = long.filter(within).map(piece)
    const away = (ribbon.away ?? [])
        .filter((range) => !isLongAway(range) && within(range))
        .map((range) => (Math.min(Math.max((range.from + range.to) / 2, from), to) - from) / span)
    return { modes, out, away }
}

/** Une part de piste : où elle commence et combien elle couvre, en fractions du segment. */
export type RailPiece<M = never> = { top: number; height: number } & ([M] extends [never] ? object : { mode: M })

export type LineCopy = {
    /** Le fait, en une ligne. */
    head: string
    /** Au plus une ligne grise : son issue, sa source, son état. */
    sub?: string
    /** Ce que le masteur a tapé : une bulle discrète. */
    message?: string
    /** Un fait tombé sans décision : en retrait. */
    quiet?: boolean
}

const TEXTS = {
    asked: "Tu as demandé",
    message: "Ton message",
    proposals: (count: number) => `${countLabel(count, "proposition")} · ta demande`,
    added: "ajoutée à sa liste",
    reviewing: "en relecture au centre",
    toDecide: "à traiter",
    proposes: (helper: string, title: string) => `${helper} propose · ${title}`,
    sent: "envoyée",
    sending: "envoi en cours",
    status: { done: "faite", current: "en cours", todo: "à faire" },
    steps: (count: number) => countLabel(count, "étape"),
    away: (minutes: number) => `Hors de l'appli · ${minutes} min`,
    join: "Connexion",
}

/** Ce que dit une ligne ; `helper` : le prénom du champion. */
export function lineCopy(line: ThreadLine, helper: string): LineCopy {
    if (line.kind === "away") return { head: TEXTS.away(Math.round(line.to - line.minute)) }
    if (line.kind === "join") return { head: TEXTS.join }
    const entry = line.entry
    switch (entry.kind) {
        case "trace":
            return { head: entry.text, sub: entry.outcome, quiet: entry.quiet }
        case "quiz":
            return { head: entry.text, sub: entry.score }
        case "helper":
            return { head: `${helper} · ${entry.title ?? entry.text}` }
        case "working":
            return { head: entry.text }
        case "tutor": {
            const settings = entry.settings ?? []
            const request = entry.request
            if (settings.length === 0 && request == null) return { head: TEXTS.message, message: entry.text }
            const duration = settings.find((setting) => setting.field === "duration")?.label
            const others = settings
                .filter((setting) => setting.field !== "duration" && setting.label !== "")
                .map((setting) => setting.label)
            const head = [TEXTS.asked, request?.modifier, duration].filter((part) => part != null).join(" · ")
            const sub = request?.source ?? (others.length > 0 ? others.join(" · ") : undefined)
            // Une demande d'avant (sans `request`) n'a que son texte : il dit ce qui a été tapé, ou la source.
            const message = request != null ? request.message : entry.text !== "" ? entry.text : undefined
            return { head, sub, message: message !== sub ? message : undefined }
        }
        case "progress":
            return { head: entry.title, sub: entry.steps.find((step) => step.state === "active")?.label }
        case "ready": {
            const sub =
                entry.decided === true ? TEXTS.added : entry.reviewing === true ? TEXTS.reviewing : TEXTS.toDecide
            return { head: TEXTS.proposals(entry.proposals.length), sub }
        }
        case "result":
            return entry.error != null
                ? { head: entry.error }
                : { head: TEXTS.proposals(entry.activities.length), sub: TEXTS.toDecide }
        case "proposal":
            return {
                head: TEXTS.proposes(helper, entry.title),
                sub: entry.state === "sent" ? TEXTS.sent : entry.state === "sending" ? TEXTS.sending : TEXTS.toDecide,
            }
        case "activity":
            return {
                head: entry.title,
                sub: [TEXTS.status[entry.status], entry.score?.text, entry.time, TEXTS.steps(entry.traces.length)]
                    .filter((part) => part != null)
                    .join(" · "),
            }
    }
}
